import React from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { Col, Row, FormCheck as Checkbox, Button, Badge } from 'react-bootstrap'
import { ColumnDescription } from 'react-bootstrap-table-next';
//import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import { FaTimes } from 'react-icons/fa/index';

import Navigation from '../../components/navigation/Navigation'
import Modal from '../../components/Modal';
import Table from '../../components/Table';
import CreateRolePermission from '../../components/administration/CreateRolePermission';
import { Updating, Success, Error, Loading } from '../../components/visuals/Visuals'
import ErrorMessage from '../../components/visuals/ErrorMessage';

import DataAccess, { Administration } from '../../lib/dataAccess/DataAccess';
import { IPermissionRole, IPermission, IUser, IUserRole } from '../../lib/types/types';
import { tryParseInt, PermissionTypes, graphQLPost } from '../../lib/utilities';
import { clearTimeout } from 'timers';
import StatusFrame from '../../components/StatusFrame';

enum UpdateFlag {
    Idle,
    Updating,
    Success,
    Error
}

interface ExtendedIUser extends IUser {
    checked: boolean,
    index: number,
    updating: UpdateFlag,
    userRoleId?: number
}

interface RouteInfo {
    roleId: string
}

interface Props extends RouteComponentProps<RouteInfo> { 
    role: string
};

interface State {
    error: string | null,
    isLoading: boolean,
    isSaving: boolean,
    pendingChanges: boolean,
    permissionRole: IPermissionRole,
    allUsers: any[],
    roleId: number,
    modified: boolean,
    showPermissionModal: boolean,
    showDeletePermissionModal: boolean,
    showDeleteRoleModal: boolean,
    selectedPermissionId: number,
    selectedUsers: ExtendedIUser[],
    permissionFilterObjects: any
};

class EditRolePage extends React.Component<Props, State>
{
    //Timers for saving to db:
    private nameTimer : any = null;

    constructor(props: Props) 
    {
        super(props)

        this.changeName = this.changeName.bind(this);
        this.changeDescription = this.changeDescription.bind(this);
        this.savePendingChanges = this.savePendingChanges.bind(this);

        this.updateUser = this.updateUser.bind(this);
        //this.changeUser = this.changeUser.bind(this);
        this.deleteRole = this.deleteRole.bind(this);
        this.upsertPermissionRole = this.upsertPermissionRole.bind(this);
        this.onPermissionsSaveComplete = this.onPermissionsSaveComplete.bind(this);
        this.deletePermission = this.deletePermission.bind(this);
        
        const roleId = tryParseInt(props.match.params.roleId, 0)


        this.state = {
            error: null,
            isLoading: true,
            isSaving: false,
            pendingChanges: false,
            permissionRole: {
                id: 0,
                name: "",
                system: false,
                description: "",
                userRole: [],
                permissions: []
            },
            allUsers: [],
            roleId: roleId,
            selectedUsers: [],
            permissionFilterObjects: {},
            showDeletePermissionModal: false,
            selectedPermissionId: -1,
            showPermissionModal: false,
            showDeleteRoleModal: false,
            modified: false
        }
    }

    componentDidMount() {
        this.fetchData();
    }

    componentDidUnmount() {
        clearInterval(this.nameTimer);
    }

    componentDidUpdate(prevProps: Props, prevState: State) { }

    async fetchData() {
        try {
            let { permissionRole, roleId } = this.state
    
            let userPromise = Administration.getUsers();
            
            const permissionFiltersPromise = Administration.getFilters();
    
            if(roleId !== 0) {
                //If given a valid roleId, fetches and overrides permissionrole loaded from state
                let rolePromise = Administration.PermissionRole.getPermissionRole(this.state.roleId);
                permissionRole = await rolePromise;
            }

            if(permissionRole === null)
                window.location.href = '/missing';
    
            const allUsers = await userPromise;
            const permissionFilters = await permissionFiltersPromise;
            const queryResults = await Promise.all(permissionFilters.map((filter) => graphQLPost(`{${filter.query}}`)));
            const permissionFilterObjects = queryResults.reduce((obj: any, r: any, i: number) => {
                const permissionFilter = permissionFilters[i];

                //Creates object lookup for naming on IDs in the filters,
                //ie. {school: {displayName: 'Skola', data: { 0: { name: 'Skola A' }, 12: { name: 'C Skolan' } }}}
                return {
                    ...obj,
                    [permissionFilter.fieldName]: { 
                        displayName: permissionFilter.displayName,
                        data: r[permissionFilters[i].queryName]
                                .reduce((o: any, e: any) => {
                                    const getName = (e: any) => {
                                        switch (permissionFilter.fieldName) {
                                            case 'student': {
                                                return `${e.firstName} ${e.lastName}`;
                                            }
                                            default: {
                                                return e.name;
                                            }
                                        }
                                    }

                                    return {
                                        ...o, 
                                        [e.id]: {name: (e.name ? e.name : getName(e))}
                                    }
                                }, {})
                    }
                }
            }, {});

            // Converts the roles user array to object for easy lookup
            const roleUsers: {[n: number]: {value: boolean, userRole: IUserRole}} = (permissionRole?.userRole || [])
                                    .reduce((o, e) => ({...o, [e.user.id]: {value: true, userRole: e}}), {})
            const selectedUsers = allUsers.map((user, index) => (
                {
                    ...user, 
                    checked: roleUsers[user.id] !== undefined ? roleUsers[user.id].value : false, 
                    index, 
                    updating: UpdateFlag.Idle,
                    userRoleId: roleUsers[user.id] !== undefined ? roleUsers[user.id].userRole.id : -1
                }))
            
            this.setState({
                isLoading: false,
                permissionRole,
                allUsers,
                selectedUsers,
                permissionFilterObjects
            });
        }
        catch(e) {
            console.error(e);
            this.setState({isLoading: false, error: e});
        }
    }

    changeName(e: React.ChangeEvent<HTMLInputElement>) {
        this.setState({ permissionRole: { ...this.state.permissionRole, name: e.target.value,  }, pendingChanges: true });
    }
    
    changeDescription(e: React.ChangeEvent<HTMLTextAreaElement>) {
        this.setState({ permissionRole: { ...this.state.permissionRole, description: e.target.value }, pendingChanges: true });
    }

    async savePendingChanges() {
        this.setState({pendingChanges: false, isSaving: true});

        const { permissionRole } = this.state;
        try {
            //Skipping the retrieval of the result, as the user may have changed stuff while the save is pending
            await Administration.PermissionRole.updatePermissionRole(permissionRole);

            this.setState({isSaving: false});
        }
        catch(error) {
            this.setState({error, isSaving: false, pendingChanges: true})
        }
    }

    changeUser(e: any, user: ExtendedIUser) {
        const {permissionRole, permissionRole: { userRole }} = this.state

        this.setState(({selectedUsers}) => ({
            modified: true,
            selectedUsers: [
                ...selectedUsers.slice(0, user.index),
                {
                    ...selectedUsers[user.index],
                    checked: !selectedUsers[user.index].checked
                },
                ...selectedUsers.slice(user.index + 1)
            ]
        }));
    }
    
    async updateUser(index: number, userId: number) {
        const { roleId } = this.state;

        //SetState below sets the prognosis and updateflag of the row.
        this.setState(({selectedUsers}) => ({
            selectedUsers: [
                ...selectedUsers.slice(0, index),
                {
                    ...selectedUsers[index],
                    //checked: !selectedUsers[index].checked,
                    updating: UpdateFlag.Updating
                },
                ...selectedUsers.slice(index + 1)
            ]
        }));

        try {
            const { selectedUsers } = this.state
            let userRoleId = selectedUsers[index]?.userRoleId

            if(selectedUsers[index].checked && userRoleId != undefined) {
                await Administration.PermissionRole.deleteUserRole(userRoleId);
            } else {
                userRoleId = await Administration.PermissionRole.addUserRole(roleId, userId);
            }

            //Setting the statusflag to 'Success'
            this.setState(({selectedUsers}) => ({ selectedUsers: [ ...selectedUsers.slice(0, index), { ...selectedUsers[index], checked: !selectedUsers[index].checked, userRoleId, updating: UpdateFlag.Success }, ...selectedUsers.slice(index + 1) ] }),
                //Callback that sets the updateFlag to Idle, else we get rendering issues when we sort the list
                () => setTimeout(() => { 
                    this.setState(({selectedUsers}) => (
                        { selectedUsers: [ 
                            ...selectedUsers.slice(0, index), 
                            { ...selectedUsers[index], updating: UpdateFlag.Idle }, 
                            ...selectedUsers.slice(index + 1) 
                        ] }));
                }, 10000)
            );
        }
        catch (error) {
            console.error(error);
            //Setting the statusflag to 'Error'
            this.setState(({selectedUsers}) => ({ error: error, selectedUsers: [ ...selectedUsers.slice(0, index), { ...selectedUsers[index], updating: UpdateFlag.Error }, ...selectedUsers.slice(index + 1) ] }));
        }
    }

    async upsertPermissionRole() {
        const { permissionRole } = this.state;

        switch(permissionRole.id) {
            case -1: { // Admin, only allowed to update users

                break;
            }
            case 0: { // New, only allowed to add role
                let newPermissionRole = await Administration.PermissionRole.addPermissionRole(permissionRole);

                this.setState({permissionRole: newPermissionRole, roleId: newPermissionRole.id, modified: false});
                break;
            }
            default: { // Update permissionrole
                
            }
        }
    }

    getUpdateIcon(updateFlag: UpdateFlag) {
        switch (updateFlag) {
            case UpdateFlag.Idle: {
                return null;
            }
            case UpdateFlag.Error: {
                return <Error errorMessage={"Fel vid sparning"} />;
            }
            case UpdateFlag.Success: {
                return <Success />;
            }
            case UpdateFlag.Updating: {
                return <Updating />;
            }
        }
    }

    async deleteRole() {
        try {
            const permission = await Administration.PermissionRole.deletePermissionRole(this.state.permissionRole);
    
            window.location.replace("/admin");
        } catch (error) {
            this.setState({error});
        }
    }

    async deletePermission() {
        try {
            const { selectedPermissionId, permissionRole: { permissions } } = this.state;
            var index = -1;
            for(var i = 0; i < permissions.length; i++ ){
                if(permissions[i].id == selectedPermissionId) {
                    index = i;
                    break;
                 }
            }
    
            const permission = await Administration.PermissionRole.deletePermission(selectedPermissionId);
    
            this.setState(({permissionRole}) => ({
                showDeletePermissionModal: false,
                permissionRole: {
                    ...permissionRole,
                    permissions: [
                        ...permissionRole.permissions.slice(0, index),
                        ...permissionRole.permissions.slice(index + 1)
                    ]
                }
            }));
        } catch (error) {
            this.setState({error});
        }
    }

    onPermissionsSaveComplete(permissions: IPermission[]) {
        this.setState(({permissionRole}) => ({
            showPermissionModal: false,
            permissionRole: {
                ...permissionRole,
                permissions: [
                    ...permissionRole.permissions,
                    ...permissions
                ]
            }
        }));
    }

    render() {
        const { 
            error, isLoading, isSaving, roleId, permissionRole, 
            selectedUsers, showPermissionModal, pendingChanges,
            showDeletePermissionModal, permissionFilterObjects,
            showDeleteRoleModal
        } = this.state

        const editDisabled = permissionRole?.system === true || permissionRole?.id === -1;
        const userEditDisabled = permissionRole?.system === true;
        const userCursorStyle = userEditDisabled ? { cursor: 'not-allowed' } : { cursor: 'pointer' };
        const userBgStyle = userEditDisabled ? { backgroundColor: '#7D7D7D' } : { backgroundColor: '#86b74b' };

        const userColumns : ColumnDescription<ExtendedIUser, any>[] = [{
                dataField: 'id',
                text: 'ID',
                hidden: true
            }, {
                dataField: 'checked',
                text: '',
                sort: false,
                //headerFormatter: (colDesc: ColumnDescription<any, any>) => (<div></div>),
                headerStyle: { width: '3rem' },
                formatter: (cell: boolean, row: ExtendedIUser) => {
                    return (<div style={{ textAlign: 'center' }} >
                        <Checkbox disabled={userEditDisabled} defaultChecked={cell}/*onChange={(e: any) => this.changeUser(e, row)}*/ />
                    </div>);
                }
            }, {
                dataField: 'userName',
                //filter: textFilter(),
                text: 'Användare',
                sort: true
            }, {
                dataField: 'email',
                //filter: textFilter(),
                text: 'E-post',
                sort: true,
                formatter: (cell, row: ExtendedIUser) => {
                    return (<div style={{ position: 'relative' }}>
                        {cell}
                        <div style={{ position: 'absolute', right: '-2.5rem', top: '0rem' }}>
                            {this.getUpdateIcon(row.updating)}
                        </div>
                    </div>)
                }
            }
        ];
        
        const permissionColumns : ColumnDescription<IPermission, any>[] = [{
                dataField: 'id',
                text: 'ID',
                hidden: true
            }, {
                dataField: 'fieldName',
                //filter: textFilter(),
                text: 'Typ',
                formatter: (cell) => {
                    return <div>{permissionFilterObjects[cell].displayName}</div>
                },
                sort: true
            }, {
                dataField: 'fieldValue',
                //filter: textFilter(),
                text: 'Namn',
                formatter: (cell, row) => {
                    return <div>{permissionFilterObjects[row.fieldName].data[cell]?.name || 'Borttagen'}</div>
                },
                sort: true
            }, {
                dataField: 'permissionTypeId',
                text: 'Typ',
                formatter: (cell) => <div>{PermissionTypes.enum[cell].displayName}</div>
            } , {
                dataField: 'edit',
                isDummyField: true,
                text: 'Ta bort',
                hidden: editDisabled,
                headerStyle: { width: '5rem', textAlign: 'center' },
                formatter: (_, row: IPermission) => {
                    return (<div 
                            style={{ textAlign: 'center', cursor: 'pointer', maxHeight: '1rem' }} 
                            onClick={(e) => this.setState({showDeletePermissionModal: true, selectedPermissionId: row.id})}
                        >
                            <FaTimes style={{width:'100%'}} size={20}/>
                    </div>);
                }
            }
        ];

        const userRowEvents = userEditDisabled ? { } : { onClick: (e: React.MouseEvent<any>, row: ExtendedIUser, rowIndex: number) => {
            this.updateUser(row.index, row.id);
            //this.changeUser(e, row)
        } }
        const isNew = roleId === 0;
        
        const menuField = [
                (<div style={{width:'100%', marginBottom:'0.6rem'}}>
                    <div style={{fontSize: '1.5rem', height:'3rem', paddingBottom: '0.5rem', display: 'inline'}}>Rättigheter</div>
                    <Button disabled={editDisabled} style={{fontSize: '1.1rem', marginLeft: '1rem', float:'right'}} onClick={() => this.setState({showPermissionModal: true})}>
                        Lägg till rättigheter
                    </Button>
                </div>)
            ];

        return (
            <div className="render-editRolePage">
                <Navigation role={ this.props.role } />
                <div className="junit-container" style={{ overflow: 'visible' }}>
                    <ErrorMessage hidden={error === null}>{error}</ErrorMessage>
                    <Modal title={"Skapa rättighet"} show={showPermissionModal} onHide={() => this.setState({showPermissionModal: false})}>
                        <CreateRolePermission roleId={roleId} onSaveComplete={this.onPermissionsSaveComplete}/>
                    </Modal>
                    <Modal title={"Ta bort rättighet"} show={showDeleteRoleModal} onHide={() => this.setState({showDeleteRoleModal: false})}>
                        <span style={{fontSize:'1.25rem', marginBottom:'1rem'}}>{"Är du säker på att du vill ta bort rättigheten från behörighetgruppen?"}</span>
                        <div style={{paddingTop:'1rem', fontSize: '1.25rem'}}>
                            <Button style={{fontSize:'1.25rem'}} onClick={this.deleteRole}>{"Ja"}</Button>
                            <Button style={{marginLeft:'1rem', fontSize:'1.25rem'}} onClick={()=>this.setState({showDeleteRoleModal: false})}>{"Nej"}</Button>
                        </div>
                    </Modal>
                    <Modal title={"Ta bort behörighetgruppen"} show={showDeletePermissionModal} onHide={() => this.setState({showDeletePermissionModal: false})}>
                        <span style={{fontSize:'1.25rem', marginBottom:'1rem'}}>{"Är du säker på att du vill ta bort behörighetgruppen?"}</span>
                        <div style={{paddingTop:'1rem', fontSize: '1.25rem'}}>
                            <Button style={{fontSize:'1.25rem'}} onClick={this.deletePermission}>{"Ja"}</Button>
                            <Button style={{marginLeft:'1rem', fontSize:'1.25rem'}} onClick={()=>this.setState({showDeletePermissionModal: false})}>{"Nej"}</Button>
                        </div>
                    </Modal>
                    <h3>{`${permissionRole.system === true ? "Visning av " : isNew ? 'Skapa ny' : 'Redigering av'} behörighetsgrupp`}</h3>
                    {/*<div className={`${editDisabled ? '' : 'hidden'}`} 
                        style={{backgroundColor:'#7D7D7D', padding:'0.75rem', color:'white', fontSize:'1.25rem', borderRadius:'0.3rem'}}
                    >
                        { `Systemroll: ${permissionRole.system === true ? 'inaktiverad' : 'limiterad'} redigering` }
                    </div>*/}
                    <Badge variant="secondary" className={`${editDisabled ? '' : 'hidden'}`} style={{fontSize:'1.5rem',padding:'0.75rem',width:'100%',textAlign:'left'}}>
                        { `Systemroll: ${permissionRole.system === true ? 'inaktiverad' : 'limiterad'} redigering` }
                    </Badge>
                    <div style={{fontSize: '1.25rem', marginBottom: '1rem'}}>
                        <div style={{}}>Namn:</div>
                        <input disabled={editDisabled} className="form-control" style={{/*fontSize: '1rem'*/}} value={permissionRole.name} onChange={this.changeName} />
                    </div>
                    <div style={{fontSize: '1.25rem', marginBottom: '1rem'}}>
                        <div style={{}}>Beskrivning:</div>
                        <textarea disabled={editDisabled} className="form-control" style={{/*fontSize: '1rem',*/ resize: 'none'}} rows={8} value={permissionRole.description || ""} onChange={this.changeDescription} />
                    </div>
                    <div style={{position: 'relative'}}>
                        {isNew ? <div style={{position:'absolute',textAlign:'center',width:'100%',zIndex:10,top:'5rem',fontSize:'1.5rem',fontWeight:'bold'}}>
                            Skapa upp behörighetsgrupp genom att skriva in namn och klicka på <Button onClick={this.upsertPermissionRole}>Skapa</Button>
                        </div> : null}
                        <div className={isNew ? 'blur' : ''}>
                            <Row>
                                <Col md="6" xs="12">
                                    <Table
                                        keyField={'id'}
                                        columns={permissionColumns}
                                        data={permissionRole.permissions || []}
                                        loading={isLoading}
                                        emptyTableText={permissionRole.id === -1 ? 'Fulla rättigheter' : undefined}
                                        //filter={filterFactory()}
                                        searchHidden={true}
                                        menuField={menuField}
                                        pageSize={10}
                                        //searchPlaceholder={"Sök användare..."}
                                    />
                                </Col>
                                <Col md="6" xs="12">
                                    <Table
                                        keyField={'id'}
                                        columns={userColumns}
                                        data={selectedUsers}
                                        loading={isLoading}
                                        //filter={filterFactory()}
                                        pageSize={10}
                                        searchPlaceholder={"Sök användare..."}
                                        bsTableProps={{
                                            rowEvents: { ...userRowEvents },
                                            rowStyle: (row:ExtendedIUser, rowIndex: number) => {
                                                if(row.checked) {
                                                    return {...userBgStyle, color: 'white', ...userCursorStyle}
                                                }
                                                else { return userCursorStyle }
                                            },
                                            defaultSorted: [{dataField:'checked', order: 'desc'}]
                                        }}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </div>
                    <Button variant="danger" style={{fontSize:'1.25rem'}} onClick={() => this.setState({showDeleteRoleModal: true})}>{"Ta bort behörighetsgrupp"}</Button>
                    <StatusFrame>
                        { isSaving ? <div>
                            <Loading>
                                {"Sparar ändringar..."}
                            </Loading>
                        </div> : null }
                        { pendingChanges ? <div style={{fontSize: '1.5rem'}}>
                            {"Ej sparade ändringar"}
                            <Button title={isSaving? "Väntar på initierad sparning..." : ""} onClick={this.savePendingChanges} disabled={isSaving} style={{marginLeft:'3rem', fontSize:'1.25rem'}}>{"Spara"}</Button>
                        </div> : null }
                    </StatusFrame>
                </div>
            </div>
        )
    }
}

export default withRouter(EditRolePage)